import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, TrendingUp, History, ArrowDownToLine, ArrowUpFromLine, DollarSign, ShieldOff, Activity } from "lucide-react";
import { api, fmtUSD } from "@/lib/api";

const TILES = [
  { k: "total_users", label: "Total Clients", icon: Users, to: "/admin/users" },
  { k: "blocked_users", label: "Blocked Clients", icon: ShieldOff, to: "/admin/users" },
  { k: "open_trades", label: "Open Trades", icon: TrendingUp, to: "/admin/trades/open" },
  { k: "closed_trades", label: "Closed Trades", icon: History, to: "/admin/trades/history" },
  { k: "pending_deposits", label: "Pending Deposits", icon: ArrowDownToLine, to: "/admin/deposits" },
  { k: "pending_withdrawals", label: "Pending Withdrawals", icon: ArrowUpFromLine, to: "/admin/withdrawals" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats) {
    return <div className="text-muted-foreground" data-testid="dashboard-loading">Loading…</div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
        <div className="bg-card p-6">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-primary" />
            <span className="label-eyebrow">Total Client Balance</span>
          </div>
          <div className="font-mono-num text-4xl font-semibold" data-testid="stat-total-balance">
            {fmtUSD(stats.total_client_balance)}
          </div>
          <div className="text-xs text-muted-foreground mt-2">Sum of all client account balances</div>
        </div>
        <div className="bg-card p-6">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-primary" />
            <span className="label-eyebrow">Booked P&amp;L (All-time)</span>
          </div>
          <div
            className={`font-mono-num text-4xl font-semibold ${stats.booked_pnl >= 0 ? "text-profit" : "text-loss"}`}
            data-testid="stat-booked-pnl"
          >
            {fmtUSD(stats.booked_pnl, { signed: true })}
          </div>
          <div className="text-xs text-muted-foreground mt-2">Cumulative P&amp;L from closed trades</div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-px bg-border">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <Link
              key={t.k}
              to={t.to}
              data-testid={`tile-${t.k}`}
              className="bg-card p-5 hover:bg-surface-hover transition-colors group"
            >
              <div className="flex items-start justify-between mb-4">
                <Icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <div className="font-mono-num text-3xl font-semibold leading-none">
                {stats[t.k]}
              </div>
              <div className="text-xs text-muted-foreground mt-2">{t.label}</div>
            </Link>
          );
        })}
      </div>

      <div className="border border-subtle p-8 bg-card">
        <p className="label-eyebrow mb-2">Quick Actions</p>
        <h3 className="text-lg font-semibold mb-4">Common operations</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/users" className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:bg-primary/90 transition-colors" data-testid="qa-add-user">
            Add New Client
          </Link>
          <Link to="/admin/trades/add" className="px-4 py-2 border border-subtle text-foreground rounded text-sm font-medium hover:bg-surface-hover transition-colors" data-testid="qa-add-trade">
            Open New Trade
          </Link>
          <Link to="/admin/trades/bulk" className="px-4 py-2 border border-subtle text-foreground rounded text-sm font-medium hover:bg-surface-hover transition-colors" data-testid="qa-bulk-upload">
            Bulk Upload Trades
          </Link>
        </div>
      </div>
    </div>
  );
}
